import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, Plus, RefreshCw, Search, ChevronDown } from 'lucide-react';
import { getPriorityList, getCaseProfile } from '../api/chatAI';
import CaseCard from './CaseCard';
import CaseProfile from './CaseProfile';

export default function LeftPanel({ selectedRecord, onSelectRecord, refreshKey, attentionData }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [recordTypes, setRecordTypes] = useState([]);
  const [sortOrder, setSortOrder] = useState('updated');
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [pendingNavigateId, setPendingNavigateId] = useState(null);
  const recordsRef = useRef(records);
  useEffect(() => { recordsRef.current = records; }, [records]);
  const onSelectRecordRef = useRef(onSelectRecord);
  useEffect(() => { onSelectRecordRef.current = onSelectRecord; }, [onSelectRecord]);

  const loadPriorityList = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getPriorityList();
      const recs = data.records || [];
      setRecords(recs);
      // Build unique record type list
      const types = [...new Set(recs.map(r => r.record_type_name))].filter(Boolean);
      setRecordTypes(types);
    } catch (err) {
      console.error('Priority list error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPriorityList(); }, [loadPriorityList, refreshKey]);

  // Handle pending navigation after records refresh
  useEffect(() => {
    if (pendingNavigateId && records.length > 0) {
      const rec = records.find(r => r.record_id === pendingNavigateId);
      if (rec) {
        setPendingNavigateId(null);
        onSelectRecord(rec);
      }
    }
  }, [records, pendingNavigateId, onSelectRecord]);

  useEffect(() => {
    if (!selectedRecord) { setProfile(null); return; }
    setProfileLoading(true);
    getCaseProfile(selectedRecord.record_id)
      .then(data => setProfile(data.profile))
      .catch(() => setProfile(null))
      .finally(() => setProfileLoading(false));
  }, [selectedRecord]);

  // Panel command event listeners (AI-controlled)
  useEffect(() => {
    const onNavigate = (e) => {
      console.log("[LeftPanel] panelNavigate received:", e.detail.record_id, "records count:", recordsRef.current.length);
      // Reset all filters so the record is visible
      setTypeFilter('all');
      setUrgencyFilter('all');
      setSearch('');
      setSortOrder('updated');
      // Use ref to get latest records after state reset
      setTimeout(() => { // wait for filter state to settle
        const rec = recordsRef.current.find(r => r.record_id === e.detail.record_id);
        if (rec) {
          onSelectRecordRef.current(rec);
        } else {
          // Record not in list yet — store target and refresh
          setPendingNavigateId(e.detail.record_id);
          loadPriorityList();
        }
      }, 300);
    };
    const onSort = (e) => setSortOrder(e.detail.sort_by);
    const onFilter = (e) => {
      if (e.detail.type_filter) setTypeFilter(e.detail.type_filter);
      if (e.detail.urgency_filter) setUrgencyFilter(e.detail.urgency_filter);
    };
    const onSearch = (e) => setSearch(e.detail.search_query);

    window.addEventListener('panelNavigate', onNavigate);
    window.addEventListener('panelSort', onSort);
    window.addEventListener('panelFilter', onFilter);
    window.addEventListener('panelSearch', onSearch);
    return () => {
      window.removeEventListener('panelNavigate', onNavigate);
      window.removeEventListener('panelSort', onSort);
      window.removeEventListener('panelFilter', onFilter);
      window.removeEventListener('panelSearch', onSearch);
    };
  }, []);

  const handleBack = () => { onSelectRecord(null); setProfile(null); };

  const handleNewIntake = () => {
    onSelectRecord(null);
    window.dispatchEvent(new CustomEvent('startIntake'));
  };

  // Filter records
  const filtered = records.filter(r => {
    const matchesType = typeFilter === 'all' || r.record_type_name === typeFilter;
    const matchesSearch = !search || r.title.toLowerCase().includes(search.toLowerCase());
    const matchesUrgency = urgencyFilter === 'all' || r.urgency === urgencyFilter;
    return matchesType && matchesSearch && matchesUrgency;
  }).sort((a, b) => {
    switch (sortOrder) {
      case 'name': return (a.title || '').localeCompare(b.title || '');
      case 'sol': {
        const aVal = a.sol_days ?? Infinity;
        const bVal = b.sol_days ?? Infinity;
        return aVal - bVal;
      }
      case 'value': {
        const aVal = a.extended_data?.estimated_value ?? -Infinity;
        const bVal = b.extended_data?.estimated_value ?? -Infinity;
        return bVal - aVal;
      }
      default: return 0; // 'updated' — already sorted by backend
    }
  });

  return (
    <div className="w-96 flex-shrink-0 bg-gray-900 border-r border-gray-800 flex flex-col overflow-hidden">
      {selectedRecord ? (
        <>
          <div className="px-4 py-3 border-b border-gray-800 flex items-center gap-2">
            <button onClick={handleBack} className="p-1 text-gray-400 hover:text-white rounded transition-colors">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <span className="text-gray-300 text-sm font-medium truncate">{selectedRecord.title}</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            {profileLoading ? (
              <div className="p-4 text-center text-gray-500 text-sm">Loading...</div>
            ) : profile ? (
              <CaseProfile profile={profile} onSelectRecord={onSelectRecord} />
            ) : (
              <div className="p-4 text-center text-gray-500 text-sm">Could not load profile</div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
            <span className="text-gray-200 text-sm font-semibold">Priority Cases</span>
            <div className="flex items-center gap-1">
              <div className="relative">
                <button onClick={() => setShowSortMenu(v => !v)} className="p-1 text-gray-500 hover:text-gray-300 rounded transition-colors flex items-center gap-0.5 text-[10px]">
                  <ChevronDown className="h-3 w-3" />
                  <span className="capitalize">{sortOrder === 'updated' ? 'Recent' : sortOrder === 'sol' ? 'SOL' : sortOrder === 'value' ? 'Value' : 'Name'}</span>
                </button>
                {showSortMenu && (
                  <div className="absolute right-0 top-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-10 py-1 min-w-[100px]">
                    {[{ v: 'updated', l: 'Recent' }, { v: 'name', l: 'Name' }, { v: 'sol', l: 'SOL' }, { v: 'value', l: 'Value' }].map(o => (
                      <button key={o.v} onClick={() => { setSortOrder(o.v); setShowSortMenu(false); }}
                        className={`block w-full text-left px-3 py-1.5 text-[11px] transition-colors ${sortOrder === o.v ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'}`}
                        style={sortOrder === o.v ? { color: '#c9a84c', background: '#1a1710' } : {}}>
                        {o.l}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={loadPriorityList} className="p-1 text-gray-500 hover:text-gray-300 rounded transition-colors">
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="px-3 py-2 border-b border-gray-800">
            <div className="flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-1.5">
              <Search className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search..."
                className="bg-transparent text-gray-300 placeholder-gray-600 text-xs flex-1 focus:outline-none"
              />
            </div>
          </div>

          {/* Type filter tabs */}
          <div className="border-b border-gray-800">
            <div className="flex gap-1 px-3 pt-2 pb-1 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
              <button
                onClick={() => setTypeFilter('all')}
                className={`px-2.5 py-1 text-[11px] rounded-full whitespace-nowrap flex-shrink-0 transition-colors ${
                  typeFilter === 'all' ? '' : 'bg-gray-800 text-gray-400 hover:text-gray-200'
                }`}
                style={typeFilter === 'all' ? { background: 'linear-gradient(135deg, #c9a84c, #a67c32)', color: '#fff8e6', borderRadius: '20px', padding: '4px 10px', fontSize: '11px', fontWeight: 600 } : {}}
              >
                All
              </button>
              {recordTypes.map(type => (
                <button
                  key={type}
                  onClick={() => setTypeFilter(type === typeFilter ? 'all' : type)}
                  className={`px-2.5 py-1 text-[11px] rounded-full whitespace-nowrap flex-shrink-0 transition-colors ${
                    typeFilter === type ? '' : 'bg-gray-800 text-gray-400 hover:text-gray-200'
                  }`}
                  style={typeFilter === type ? { background: 'linear-gradient(135deg, #c9a84c, #a67c32)', color: '#fff8e6', borderRadius: '20px', padding: '4px 10px', fontSize: '11px', fontWeight: 600 } : {}}
                >
                  {type}
                </button>
              ))}
            </div>
            {(typeFilter !== 'all' || urgencyFilter !== 'all' || search !== '' || sortOrder !== 'updated') && (
              <div className="flex justify-end px-3 pb-1.5">
                <button
                  onClick={() => { setTypeFilter('all'); setUrgencyFilter('all'); setSearch(''); setSortOrder('updated'); }}
                  className="text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 transition-colors font-medium"
                  style={{ background: '#1a1710', color: '#c9a84c', border: '1px solid #3d3418', borderRadius: '20px', padding: '4px 12px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', cursor: 'pointer' }}
                >
                  <span>✕</span>
                  <span>Reset filters</span>
                </button>
              </div>
            )}
          </div>

          {/* Case list */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500 text-sm">Loading cases...</div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">No records found</div>
            ) : (
              filtered.map(r => (
                <CaseCard key={r.record_id} record={r} badge={attentionData?.record_badges?.[r.record_id]} onClick={() => onSelectRecord(r)} />
              ))
            )}
          </div>

          {/* New intake button */}
          <div className="p-3 border-t border-gray-800">
            <button onClick={handleNewIntake}
              className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded-xl transition-colors"
              style={{ background: 'linear-gradient(135deg, #c9a84c, #a67c32)', color: '#fff8e6', border: '1px solid #d4b060' }}>
              <Plus className="h-4 w-4" /> New Intake
            </button>
          </div>
        </>
      )}
    </div>
  );
}
